import { Box, Typography } from '@mui/material'
import type { FC } from 'react'
import type { HandleDirection } from './useBBoxInteractions'

type Props = {
  x: number
  y: number
  width: number
  height: number
  color: string
  dashed?: boolean
  label: string
  onPointerDownMove: (e: React.PointerEvent<HTMLElement>) => void
  onPointerMoveActive: (e: React.PointerEvent<HTMLElement>) => void
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void
  onPointerDownHandle: (
    e: React.PointerEvent<HTMLElement>,
    dir: HandleDirection,
  ) => void
}

/** 操作中のBBox。ハンドルでリサイズしたり、移動する。 */
export const InteractiveBBox: FC<Props> = ({
  x,
  y,
  width,
  height,
  color,
  dashed,
  label,
  onPointerDownMove,
  onPointerMoveActive,
  onPointerUp,
  onPointerDownHandle,
}) => {
  const borderStyle = `2px ${dashed ? 'dashed' : 'solid'} ${color}`

  return (
    <Box
      sx={{
        position: 'absolute',
        border: borderStyle,
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        width: `${width * 100}%`,
        height: `${height * 100}%`,
        cursor: 'move',
      }}
      onPointerDown={onPointerDownMove}
      onPointerMove={onPointerMoveActive}
      onPointerUp={onPointerUp}
    >
      {label.length > 0 && (
        <Typography
          sx={{
            position: 'absolute',
            top: '-20px',
            left: '0',
            backgroundColor: color,
            padding: '2px 4px',
            fontSize: '12px',
            borderRadius: '4px',
            textWrap: 'nowrap',
            color: 'white',
          }}
        >
          {label}
        </Typography>
      )}
      {(
        [
          { key: 'nw', style: { left: -4, top: -4 }, cursor: 'nwse-resize' },
          { key: 'ne', style: { right: -4, top: -4 }, cursor: 'nesw-resize' },
          {
            key: 'sw',
            style: { left: -4, bottom: -4 },
            cursor: 'nesw-resize',
          },
          {
            key: 'se',
            style: { right: -4, bottom: -4 },
            cursor: 'nwse-resize',
          },
          {
            key: 'n',
            style: { left: '50%', top: -4, transform: 'translateX(-50%)' },
            cursor: 'ns-resize',
          },
          {
            key: 's',
            style: { left: '50%', bottom: -4, transform: 'translateX(-50%)' },
            cursor: 'ns-resize',
          },
          {
            key: 'w',
            style: { left: -4, top: '50%', transform: 'translateY(-50%)' },
            cursor: 'ew-resize',
          },
          {
            key: 'e',
            style: { right: -4, top: '50%', transform: 'translateY(-50%)' },
            cursor: 'ew-resize',
          },
        ] as const
      ).map(h => (
        <Box
          key={h.key}
          sx={{
            position: 'absolute',
            width: 8,
            height: 8,
            backgroundColor: color,
            borderRadius: '2px',
            boxShadow: 1,
            cursor: h.cursor,
            ...h.style,
          }}
          onPointerDown={e => onPointerDownHandle(e, h.key as HandleDirection)}
          onPointerMove={onPointerMoveActive}
          onPointerUp={onPointerUp}
        />
      ))}
    </Box>
  )
}
